import React, { useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { QuotaInfo } from '../services/api';

// Model option type
export interface ModelOption {
  provider: string;
  model: string;
  apiVersion: string;
  description: string;
  isAnthropic?: boolean;
  isAdvanced?: boolean;
  can_use?: boolean;
}

interface ModelSelectorProps {
  modelOptions: ModelOption[];
  selectedModelIndex: number;
  onModelSelect: (index: number) => void;
  onToggleConversations?: () => void;
  isSidebarOpen?: boolean;
  compact?: boolean;
  isLoading?: boolean;
  error?: string | null;
  quota?: QuotaInfo | null;
  // Bumped by the caller to open the dropdown from elsewhere (the quota nudge).
  openRequest?: number;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({
  modelOptions,
  selectedModelIndex,
  onModelSelect,
  onToggleConversations,
  isSidebarOpen = false,
  compact = false,
  isLoading = false,
  error = null,
  quota = null,
  openRequest = 0,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);
  const [showDescriptionModal, setShowDescriptionModal] = React.useState<boolean>(false);
  const [selectedDescription, setSelectedDescription] = React.useState<string>('');
  const [selectedModelName, setSelectedModelName] = React.useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { colors, theme } = useTheme();

  useEffect(() => {
    if (openRequest > 0) setIsDropdownOpen(true);
  }, [openRequest]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleModelSelect = (index: number) => {
    if (modelOptions[index]?.can_use === false) return;
    onModelSelect(index);
    setIsDropdownOpen(false);
  };

  const handleShowDescription = (e: React.MouseEvent, option: ModelOption) => {
    e.stopPropagation();
    setSelectedDescription(option.description);
    setSelectedModelName(option.model);
    setShowDescriptionModal(true);
  };

  return (
    <div
      style={{
        position: compact ? 'relative' : 'absolute',
        top: compact ? 'auto' : '20px',
        left: compact ? 'auto' : (isSidebarOpen ? '340px' : '80px'),
        zIndex: compact ? 'auto' : 100,
        display: 'flex',
        alignItems: 'center',
        gap: compact ? '8px' : '12px',
        transition: compact ? 'none' : 'left 0.3s ease-in-out'
      }}>

      {/* Model Selector Dropdown */}
      <div
        ref={dropdownRef}
        style={{
          position: 'relative'
        }}>
      {/* Dropdown Trigger */}
      <div
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        style={{
          padding: compact ? '8px 12px' : '12px 16px',
          borderRadius: compact ? '8px' : '12px',
          background: compact ? colors.surface : colors.surface,
          backdropFilter: 'blur(20px)',
          border: compact ? `1px solid ${colors.border}` : `1px solid ${colors.border}`,
          boxShadow: compact ? (theme === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.3)') : (theme === 'light' ? '0 8px 32px rgba(0, 0, 0, 0.1)' : '0 8px 32px rgba(0, 0, 0, 0.3)'),
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: compact ? '6px' : '8px',
          minWidth: compact ? '180px' : '220px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: compact ? '13px' : '14px',
          fontWeight: 500,
          color: colors.textPrimary,
          userSelect: 'none'
        }}
        onMouseEnter={(e) => {
          if (!compact) {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = theme === 'light' ? '0 12px 40px rgba(0, 0, 0, 0.15)' : '0 12px 40px rgba(0, 0, 0, 0.4)';
            e.currentTarget.style.background = colors.surfaceElevated;
          } else {
            e.currentTarget.style.background = colors.surfaceElevated;
          }
        }}
        onMouseLeave={(e) => {
          if (!compact) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = theme === 'light' ? '0 8px 32px rgba(0, 0, 0, 0.1)' : '0 8px 32px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.background = colors.surface;
          } else {
            e.currentTarget.style.background = colors.surface;
          }
        }}
      >
        <div style={{
          width: compact ? '6px' : '8px',
          height: compact ? '6px' : '8px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          boxShadow: compact ? '0 0 4px rgba(16, 185, 129, 0.3)' : '0 0 8px rgba(16, 185, 129, 0.3)'
        }} />
        <span style={{ flex: 1 }}>
          {isLoading 
            ? 'Loading models...' 
            : error 
              ? 'Error loading models' 
              : (!modelOptions || modelOptions.length === 0) 
                ? 'No models available'
                : (modelOptions[selectedModelIndex]?.model || 'Default model')}
        </span>
        <svg
          width={compact ? "14" : "16"}
          height={compact ? "14" : "16"}
          viewBox="0 0 24 24"
          fill="none"
          style={{
            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Dropdown Menu */}
      {isDropdownOpen && !isLoading && !error && modelOptions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '0',
          right: '0',
          marginTop: '4px',
          background: colors.surface,
          backdropFilter: 'blur(20px)',
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          boxShadow: theme === 'light' ? '0 20px 50px rgba(0, 0, 0, 0.15)' : '0 20px 50px rgba(0, 0, 0, 0.4)',
          overflow: 'hidden',
          animation: 'dropdownSlide 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          transformOrigin: 'top'
        }}>
          <style>{`
            @keyframes dropdownSlide {
              from {
                opacity: 0;
                transform: translateY(-8px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}</style>
          {modelOptions.map((option, index) => {
            const isQuotaExhausted =
              option.isAdvanced && quota && !quota.is_unlimited && quota.used >= quota.limit;
            const isDisabled = option.can_use === false || !!isQuotaExhausted;
            const quotaTooltip = isQuotaExhausted
              ? `Monthly quota reached (${quota!.used}/${quota!.limit}). Resets ${quota!.resets_at}.`
              : undefined;
            const tooltipText = isDisabled
              ? (quotaTooltip || 'You do not have access to this model')
              : undefined;

            return (
            <div
              key={index}
              onClick={() => handleModelSelect(index)}
              title={tooltipText}
              style={{
                padding: '12px 16px',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                fontWeight: 500,
                color: isDisabled ? colors.textMuted : selectedModelIndex === index ? '#2563eb' : colors.textSecondary,
                background: selectedModelIndex === index && !isDisabled ? 'rgba(37, 99, 235, 0.05)' : 'transparent',
                transition: 'all 0.15s ease',
                fontFamily: 'Inter, system-ui, sans-serif',
                borderLeft: selectedModelIndex === index && !isDisabled ? '3px solid #2563eb' : '3px solid transparent',
                opacity: isDisabled ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!isDisabled && selectedModelIndex !== index) {
                  e.currentTarget.style.background = colors.surfaceElevated;
                  e.currentTarget.style.color = colors.textPrimary;
                }
              }}
              onMouseLeave={(e) => {
                if (!isDisabled && selectedModelIndex !== index) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = colors.textSecondary;
                }
              }}
            >
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: selectedModelIndex === index
                  ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                  : colors.border,
                transition: 'all 0.2s ease'
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>{option.model}</span>
                  {option.isAdvanced && quota && !quota.is_unlimited && (
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '1px 5px',
                      borderRadius: '4px',
                      background: isQuotaExhausted ? 'rgba(239,68,68,0.12)' : 'rgba(37,99,235,0.10)',
                      color: isQuotaExhausted ? '#ef4444' : '#2563eb',
                      whiteSpace: 'nowrap',
                    }}>
                      {quota.used}/{quota.limit}
                    </span>
                  )}
                </div>
                {option.isAdvanced && quota && !quota.is_unlimited && (
                  <div style={{
                    marginTop: '4px',
                    height: '3px',
                    borderRadius: '2px',
                    background: colors.border,
                    overflow: 'hidden',
                    width: '100%',
                  }}>
                    <div style={{
                      height: '100%',
                      borderRadius: '2px',
                      width: `${Math.min((quota.used / quota.limit) * 100, 100)}%`,
                      background: isQuotaExhausted
                        ? '#ef4444'
                        : quota.used / quota.limit > 0.8
                          ? '#f59e0b'
                          : '#2563eb',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                )}
              </div>
              {selectedModelIndex === index && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              <div
                onClick={(e) => handleShowDescription(e, option)}
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: colors.surfaceElevated,
                  border: `1px solid ${colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: colors.textSecondary,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.primary;
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = colors.surfaceElevated;
                  e.currentTarget.style.color = colors.textSecondary;
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                i
              </div>
            </div>
            );
          })}
        </div>
      )}
      </div>

      {/* Description Modal */}
      {showDescriptionModal && (
        <div 
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowDescriptionModal(false)}
        >
          <div 
            style={{
              background: colors.surface,
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: theme === 'light' ? '0 20px 50px rgba(0, 0, 0, 0.2)' : '0 20px 50px rgba(0, 0, 0, 0.5)',
              border: `1px solid ${colors.border}`,
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h3 style={{
                margin: '0',
                fontSize: '18px',
                fontWeight: '600',
                color: colors.textPrimary,
                fontFamily: 'Inter, system-ui, sans-serif'
              }}>
                {selectedModelName}
              </h3>
              <button
                onClick={() => setShowDescriptionModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: colors.textSecondary,
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.surfaceElevated;
                  e.currentTarget.style.color = colors.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = colors.textSecondary;
                }}
              >
                ×
              </button>
            </div>
            <p style={{
              margin: '0',
              fontSize: '14px',
              lineHeight: '1.5',
              color: colors.textSecondary,
              fontFamily: 'Inter, system-ui, sans-serif'
            }}>
              {selectedDescription}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelSelector;